<script>
  import { getContext } from 'svelte';
  import { format } from 'svelte-i18n';
  import { link, navigate, useLocation, useParams } from 'svelte-navigator';
  import {
    Breadcrumb,
    BreadcrumbItem,
    ButtonDropdown,
    Col,
    Container,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Row
  } from '@sveltestrap/sveltestrap';
  import { createQuery } from '@tanstack/svelte-query';

  import { DomainCard } from '@client/components/domain-card';
  import { CloneDomainModal } from '@client/components/domain-card/modals';
  import { Loader } from '@client/components/loader';
  import { getNotificationsContext } from '@client/components/notifications';

  import { DomainService } from '@client/services/domain';
  import { DomainsService } from '@client/services/domains';

  import { switchDomain } from '@client/stores/domain-switch';
  import { userStore } from '@client/stores/user';

  import { DomainReports } from './reports';
  import { UsageTable } from './usage';

  const { basepath } = getContext('config');

  const { addNotification } = getNotificationsContext();

  const location = useLocation();
  const params = useParams();
  const domainname = $params.id;
  const isStaff = $location.pathname === `/staff/domains/${domainname}`;

  const labels = {
    signature: 'Signature',
    pipeline: 'Pipeline',
    complete: 'Complete',
    engagement: 'Engagment'
  };

  let invoices = [];
  let usage = [];
  let showCloneDomainModal = false;
  let domain = {};
  let usageData = {};

  // Receives a domain name string, not a domain object.
  const onManageClick = async (domainname) => {
    const result = await switchDomain(domainname);

    if (!result.ok && result.reason !== 'superseded') {
      const label = result.reason === 'not-eligible' ? 'label.SWITCH_DOMAIN_NOT_ALLOWED' : 'label.SWITCH_DOMAIN_FAILED';

      addNotification({
        text: $format(label, { values: { domain: domainname } }),
        type: 'error',
        autohide: 2500,
        position: 'top-right'
      });
    }
  };

  const onClearDomainCache = async (domainname) => await DomainsService.clearDomainCache({ domainname });

  const requestInvoices = async ({ domainname }) => {
    invoices = await DomainsService.getInvoices({ domainname });
    return invoices;
  };

  const requestUsage = async ({ domainname }) => {
    const response = await DomainsService.getUsage({ domainname });

    usage = ['signature', 'pipeline', 'complete', 'engagement'].map((tier) => ({
      tier: labels[tier],
      active: response[tier],
      sold: response[`${tier}_users_sold`],
      diff: response[`${tier}_users_sold`] - response[tier]
    }));

    return usage;
  };

  const getEBCData = async (domain) => {
    const response = await DomainService.getEBC();

    if (response) {
      const data = response?.billingidSeats;

      if (data) {
        usageData = data['60'][domain?.domainsettings?.billing_id] || {};
      }
    }

    return response;
  };

  const query = createQuery({
    queryKey: ['edit-domain'],
    queryFn: async () => {
      let domainmeta;

      try {
        domainmeta = await DomainsService.getDomainReport({ domainname });
      } catch (e) {
        domainmeta = null;
      }

      if (!domainmeta) {
        const redirectUrl = isStaff ? `${basepath}/staff/domains` : `${basepath}/manage/domains`;
        navigate(redirectUrl);
        return;
      }

      // requestInvoices({ domainname });
      requestUsage({ domainname });
      // ebcdata is a staff-only backend report; it 404s by design on the /manage route
      // (mailapi/models/user.js). isStaff here is route-based, so only the /staff edit
      // page requests it — the /manage edit page no longer fires a guaranteed 404.
      if (isStaff) {
        getEBCData(domainmeta);
      }

      return domainmeta;
    }
  });

  const onCloneDomain = () => {
    showCloneDomainModal = true;
  };

  $: domain = $query.data;
</script>

<div class="page">
  <Loader backdrop show={$query.isLoading || $query.isFetching} />
  <div class="page-header">
    <Container fluid>
      <Row>
        <Col md="12">
          <Breadcrumb>
            {#if isStaff}
              <BreadcrumbItem>
                <a href="{basepath}/staff" use:link>{$format('label.STAFF_TOOLS')}</a>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <a href="{basepath}/staff/domains" use:link>{$format('label.DOMAINS')}</a>
              </BreadcrumbItem>
            {:else}
              <BreadcrumbItem>
                <a href="{basepath}/manage" use:link>{$format('label.MANAGE')}</a>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <a href="{basepath}/manage/domains" use:link>{$format('label.DOMAINS')}</a>
              </BreadcrumbItem>
            {/if}
            <BreadcrumbItem active>{$format('label.EDIT_DOMAIN')}</BreadcrumbItem>
          </Breadcrumb>
        </Col>
      </Row>
      <Row class="flex-align--center">
        <Col sm="9" md="7" xs="8">
          <h1 class="page-title">{domainname}</h1>
        </Col>
        <Col sm="3" md="5" xs="4" class="text-right">
          <ButtonDropdown>
            <DropdownToggle color="secondary" caret>{$format('label.ACTIONS')}</DropdownToggle>
            <DropdownMenu>
              {#if $userStore.domain !== domainname}
                <DropdownItem on:click={() => onManageClick(domainname)}>{$format('label.MANAGE_DOMAIN')}</DropdownItem>
                <DropdownItem divider />
              {/if}
              <DropdownItem on:click={onCloneDomain}>{$format('label.CLONE_DOMAIN')}</DropdownItem>
              <DropdownItem divider />
              <DropdownItem on:click={() => onClearDomainCache(domainname)}>
                {$format('label.CLEAR_CACHE')}
              </DropdownItem>
            </DropdownMenu>
          </ButtonDropdown>
        </Col>
      </Row>
    </Container>
  </div>

  {#if !$query.isFetching}
    <div class="page-content">
      <Container fluid>
        <Row class="pt-4">
          <div class="domain-edit">
            <DomainCard
              panel
              domain={{ ...domain, ...domain.domainsettings, outlookaddincount: domain.outlookaddin }}
              bind:usage={usageData}
            />

            <h3>{$format('label.LICENSES')}</h3>
            <div class="section">
              {#if !usage?.message}
                <UsageTable rows={usage} />
              {:else}
                <div class="empty">{$format('label.NO_LICENSING_USAGE_DATA')}</div>
              {/if}
            </div>

            <!-- <h3>{$format('label.INVOICES')}</h3>
            <div class="section">
              {#if !invoices?.message}
                <InvoicesTable rows={invoices} />
              {:else}
                <div class="empty">{$format('label.NO_INVOICE_HISTORY')}</div>
              {/if}
            </div> -->
          </div>
        </Row>
      </Container>

      {#if isStaff && $userStore.domain === domainname}
        <DomainReports {domain} />
      {/if}
    </div>
  {/if}
</div>

{#if showCloneDomainModal}
  <CloneDomainModal bind:show={showCloneDomainModal} domain={{ ...domain.domainsettings }} />
{/if}

<style lang="scss" src="./edit.scss"></style>
